import { useState } from 'react';
import type { ReactElement } from 'react';
import Button from 'react-bootstrap/Button';

import { get, post } from 'utils/network';
import { useUpcomingMenu } from 'contexts/UpcomingMenu';
import RadioButton from 'components/shared/RadioButton';
import Modal from 'components/shared/Modal';

import { ApiRoute, HeapCustomEvent } from 'config';

import IApiResponse from 'models/IApiResponse';
import { IUpcomingMenu } from 'models/UpcomingMenu';
import FulfillmentStatus from 'models/FulfillmentStatus';
import useCurrentUser from 'hooks/useCurrentUser';
import useUpcomingMenus from 'hooks/useUpcomingMenus';
import HeapAnalytics from 'services/HeapAnalytics';
import ThankYouModal from './ThankYouModal';

type Props = {
  show: boolean;
  onClose: () => void;
};

enum SkipOptions {
  ON_DEMAND = 'on-demand',
  WEEKLY = 'weekly'
}

function AutoSkipModal({ show, onClose }: Props): ReactElement {
  const { upcomingMenu, dispatch } = useUpcomingMenu();
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { upcomingMenus, reload: reloadUpcomingMenus } = useUpcomingMenus();
  const [selectedOption, setSelectedOption] = useState<SkipOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [showThanksModal, setShowThanksModal] = useState(false);

  const onSelectionChange = (e) => setSelectedOption(e.target.id);

  const unskipSingleWeek = async () => {
    setLoading(true);
    const action = upcomingMenu.isEnabled ? 'skip' : 'unskip';
    dispatch({ type: 'toggleEnabled', isEnabled: !upcomingMenu.isEnabled });

    const { ok } = await post<IApiResponse>(
      `${ApiRoute.CARTS}/${upcomingMenu.cart_id}/${action}`,
    );

    if (ok) {
      dispatch({
        type: 'reload',
        // @ts-expect-error TODO: fix null safety
        data: (await get<IUpcomingMenu>(
          `${ApiRoute.UPCOMING_MENUS}/${upcomingMenu.menu_id}`,
        )).parsedBody,
      });
    } else {
      dispatch({ type: 'toggleEnabled', isEnabled: upcomingMenu.isEnabled });
    }
    setLoading(false);
  };

  const turnOffAutoSkip = async () => {
    setLoading(true);
    const updateUserSubscription = post(`${ApiRoute.USERS}/${currentUser.id}/subscription`, {
      subscription: {
        delivery_frequency: 'weekly',
      },
    });

    const actions = upcomingMenus
      .filter((menu) => menu.fulfillment_status === FulfillmentStatus.SKIPPED)
      .map((menu) => post<IApiResponse>(
        `${ApiRoute.CARTS}/${menu.cart_id}/unskip`,
      ));

    await Promise.all([...actions, updateUserSubscription]);

    reloadUser();
    reloadUpcomingMenus();
    setLoading(false);
  };

  const onSubmit = async () => {
    let heapEvent;
    switch (selectedOption) {
      case SkipOptions.ON_DEMAND:
        heapEvent = HeapCustomEvent.AUTO_SKIP_MODAL_ORDER_ON_DEMAND;
        await unskipSingleWeek();
        break;
      case SkipOptions.WEEKLY:
        heapEvent = HeapCustomEvent.AUTO_SKIP_MODAL_WEEKLY_ORDER;
        await turnOffAutoSkip();
        break;
    }
    HeapAnalytics.track(heapEvent);
    setShowThanksModal(true);
    onClose();
  };

  const onCloseThanksModal = () => {
    setSelectedOption(null);
    setShowThanksModal(false);
  };

  return (
    <>
      <Modal
        show={show || loading}
        onClose={onClose}
        className="c-modal-auto-skip-frequency"
        header={<Modal.Header>Set Your Order Frequency</Modal.Header>}
        body={(
          <form onSubmit={(e) => e.preventDefault()}>
            <fieldset className="c-form-group">
              <legend>
                How would you like to manage your subscription?
              </legend>
              <div className="c-radio-buttons-group">
                <RadioButton
                  id={SkipOptions.ON_DEMAND}
                  label="One Time Purchase"
                  name="frequency"
                  onChange={onSelectionChange}
                />
                <RadioButton
                  id={SkipOptions.WEEKLY}
                  label="Weekly Deliveries (skip as needed)"
                  name="frequency"
                  onChange={onSelectionChange}
                />
              </div>
            </fieldset>
          </form>
        )}
        footer={(
          <Button
            className="c-button c-button--primary"
            onClick={onSubmit}
            disabled={selectedOption === null || !currentUser || loading}
          >
            Confirm
          </Button>
        )}
      />
      {
        selectedOption
        && (
        <ThankYouModal
          show={showThanksModal}
          onClose={onCloseThanksModal}
          contentType={selectedOption}
        />
        )
      }
    </>
  );
}

export default AutoSkipModal;
